import { getSystemPrompt, getInitialFinalSystemPrompt, getInitialFinalCSSSystemPrompt, getInitialFinalJSSystemPrompt } from './systemPrompt';

// How many chat messages travel with each question as context memory.
const HISTORY_WINDOW = 6;

// Chat messages as model turns: the AI agent's (sender -4) are the assistant's, the rest the student's.
const toChatTurns = (messages) => messages.map(m => ({
    role: m.sender_id === -4 ? "assistant" : "user",
    content: m.content
}));

// The new question with its context, as one user turn - plus one image_url block per
// attached frame (see below).
const contextTurn = (contextualPrompt, imageDataUrls) => ({
    role: "user",
    content: imageDataUrls?.length
        ? [
            { type: "text", text: contextualPrompt },
            ...imageDataUrls.map((url) => ({ type: "image_url", image_url: { url } })),
        ]
        : contextualPrompt
});

// imageDataUrls is populated in one of two cases (see ChatContext.js): the student clicked the
// screenshot/record button (always allowed - a deliberate, visible action for one message), or
// the session has both visualTraceEnabled and aiVisualContextEnabled on (background capture,
// sending student screen content to the LLM provider as a separate, explicit opt-in from just
// capturing it for the dataset). A burst/recording contributes one image_url block per frame -
// GPT-4o accepts multiple images per message but not literal video.
//
// language is computed by the caller (ChatContext.js), not re-derived here - it needs to check
// getGuiSessionConfig(session) first for GUI sessions (html/css/javascript/integrated), falling
// back to getSessionLanguage(sessionId) only for non-GUI sessions, the same pattern already used
// in promptContextBuilder.js. Resolving it here from a bare sessionId previously always missed
// the GUI case and silently defaulted every GUI session's system prompt to Python.
export const prepareOpenAIMessages = (history, contextualPrompt, language, imageDataUrls) => {
    // 'html-target'/'css-target'/'js-target' are only set by guiSessionConfig.js's
    // InitialFinal / InitialFinalCSS / InitialFinalJS types' `phase` - routed to a
    // dedicated, non-Socratic prompt (see systemPrompt.js's comment for why this can't
    // just be a branch inside getSystemPrompt).
    const systemPromptContent = language === 'html-target'
        ? getInitialFinalSystemPrompt()
        : language === 'css-target'
        ? getInitialFinalCSSSystemPrompt()
        : language === 'js-target'
        ? getInitialFinalJSSystemPrompt()
        : getSystemPrompt(language)

    return [
        // 1. Start with the System Persona
        { role: "system", content: systemPromptContent },

        // 2. Append History (Slice last 6 for context memory)
        ...toChatTurns(history.slice(-HISTORY_WINDOW)),

        // 3. Append the new User Question + Context
        contextTurn(contextualPrompt, imageDataUrls)
    ];
};

// A classroom-study condition's turns (workspace/study/studyConfig.js). The server builds that
// tutor's system prompt, so none is sent. `earlierMessages` is the chat BEFORE this question:
// the question itself arrives once, opening the context turn, where prepareOpenAIMessages also
// repeats it as the last history turn. Lines this page adds on its own (clientOnly - the
// greeting and the "couldn't answer" notice) are left out: the tutor never said them.
export const prepareStudyMessages = (earlierMessages, contextualPrompt, imageDataUrls) => [
    ...toChatTurns(earlierMessages.filter(m => !m.clientOnly).slice(-HISTORY_WINDOW)),
    contextTurn(contextualPrompt, imageDataUrls)
];
